'use client';

import React, { useRef, useLayoutEffect, useState, useEffect } from 'react';
import LinesEllipsis from 'react-lines-ellipsis';
import { useMediaQuery } from 'react-responsive';
import IMAGE_SIZE from '../../constants/image-size.constants';
import { getImageUrl } from '@/services/media.service';
import Link from 'next/link';
import Picture from '../Picture';
import { truncateWithEllipsis } from '@/utils/ellipsis';
import type { TypeArticleWithoutUnresolvableLinksResponse } from '@/contentful/types/TypeArticle';

type TopStoriesProps = {
  content?: TypeArticleWithoutUnresolvableLinksResponse;
  isMainArticle?: boolean;
  priorityImage?: boolean;
};

const TopStories = ({
  content,
  isMainArticle = false,
  priorityImage = false
}: TopStoriesProps) => {
  const isMobile = useMediaQuery({ query: '(max-width: 1050px)' });
  const boxSize = isMainArticle ? 'lg' : 'md';

  const lineHeight = isMobile ? 26 : 34;
  const featureImageUrl =
    content?.fields.featureImage?.fields.image?.fields.file?.url;

  const url = getImageUrl(featureImageUrl ?? '');
  const detailsRef = useRef<null | HTMLDivElement>(null);
  const titleRef = useRef<null | HTMLDivElement>(null);

  const [detailDimensions, setDetailDimensions] = useState({
    width: 0,
    height: 0
  });
  const [titleDimensions, setTitleDimensions] = useState({
    width: 0,
    height: 0
  });

  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    if (detailsRef.current) {
      setDetailDimensions({
        width: detailsRef.current.offsetWidth,
        height: detailsRef.current.offsetHeight
      });
    }
    if (titleRef.current) {
      setTitleDimensions({
        width: titleRef.current.offsetWidth,
        height: titleRef.current.offsetHeight
      });
    }
  }, [width]);

  const handleResize = () => setWidth(window.innerWidth);
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  const numberOfLines = Math.ceil(
    (detailDimensions.height - titleDimensions.height) / lineHeight
  );

  const articleAuthorInfo = (
    <>
      {content?.fields.authors && content.fields.authors.length > 0 && (
        <div className='top-stories__box__author'>
          {content.fields.authors
            .map(
              (author) =>
                `${author?.fields.firstName} ${author?.fields.lastName}`
            )
            .join(' AND ')}
        </div>
      )}
    </>
  );

  const getHeadingFontSize = (text: string) => {
    const maxSmTextLength = isMobile ? 80 : 70;
    if (text?.length && text.length > maxSmTextLength) {
      return 'titleSizeSm';
    }
    return 'titleSizeLg';
  };

  return (
    <div
      className={`top-stories__box ${isMainArticle ? 'main-article' : 'side-box'}`}
    >
      <Link
        prefetch={false}
        href={`/articles/${content?.fields.slug}`}
        className={`top-stories__box--${boxSize}`}
      >
        {/* <div className={`top-stories__box--${boxSize}`}> */}
        {url ? (
          <div className={`top-stories__box--${boxSize}__picture-container`}>
            <Picture
              src={url.href}
              width={
                isMainArticle
                  ? IMAGE_SIZE.TOP_STORIES__LARGE
                  : IMAGE_SIZE.TOP_STORIES__SMALL
              }
              priority={priorityImage}
            />
          </div>
        ) : (
          <div className='top-stories__media-container'>
            <div
              className=''
              style={{
                backgroundImage: 'url("/static/imgs/trending/trending.jpg")'
              }}
            >
              {}
            </div>
          </div>
        )}
        <div
          ref={detailsRef}
          className={`top-stories__box--${boxSize}__details`}
        >
          {!isMainArticle && (
            <div className='top-stories__box__category'>
              {content?.fields?.primarySector?.fields.name ?? ''}
            </div>
          )}

          {isMainArticle ? (
            <div
              ref={titleRef}
              className={`top-stories__box--lg__title top-stories__box--lg__${getHeadingFontSize(
                content?.fields.articleName ?? ''
              )}`}
            >
              {content?.fields.articleName}
            </div>
          ) : (
            <div className='top-stories__box--md__title'>
              {content?.fields.articleName}
            </div>
          )}
          {!isMainArticle && articleAuthorInfo}
          {isMainArticle && !!content?.fields.summary && (
            <div className='top-stories__box__desc'>
              <LinesEllipsis
                text={truncateWithEllipsis(content.fields.summary, 130)}
                maxLine={numberOfLines === 1 ? 1 : 2}
                ellipsis='...'
                trimRight
                basedOn='words'
              />
            </div>
          )}
        </div>
        {/* </div> */}
      </Link>
    </div>
  );
};

export default TopStories;
